import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Rating,
  Chip,
  Button
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiService } from '../../utils/api';

/**
 * ReviewAnalytics - クチコミ分析管理用コンポーネント（管理者向け）
 */
export default function ReviewAnalytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [topRanking, setTopRanking] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [recalculating, setRecalculating] = useState(false);
  const [recalcMessage, setRecalcMessage] = useState('');

  useEffect(() => {
    fetchReviewAnalytics();
  }, []);

  const handleRecalculate = async () => {
    try {
      setRecalculating(true);
      setRecalcMessage('');
      setError('');
      const res = await apiService.post('/api/reviews/batch-update');
      const data = res?.data || res || {};
      setRecalcMessage(`スコアを再計算しました（${data.updated || 0}件更新）`);
      // 再計算後にランキングを再取得
      await fetchReviewAnalytics();
    } catch (err) {
      setError(err.response?.data?.error || 'スコアの再計算に失敗しました');
    } finally {
      setRecalculating(false);
    }
  };

  const fetchReviewAnalytics = async () => {
    try {
      setLoading(true);
      setError('');

      // トップランキングを取得
      // apiService.get は axios レスポンスを返すため、ボディは response.data に入る。
      // 念のため response 直下も許容して防御的に取り出す。
      const rankingResponse = await apiService.get('/api/reviews-ranking', {
        params: { limit: 10 }
      });
      const rankingData = rankingResponse?.data || rankingResponse || {};
      setTopRanking(rankingData.ranking || []);

    } catch (err) {
      console.error('Fetch analytics error:', err);
      setError(err.response?.data?.error || 'クチコミ分析の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {recalcMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {recalcMessage}
        </Alert>
      )}

      {/* スコア再計算ボタン */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={recalculating ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={handleRecalculate}
          disabled={recalculating}
        >
          {recalculating ? '再計算中...' : 'スコアを再計算'}
        </Button>
      </Box>

      {/* トップランキング */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="⭐ クチコミランキングトップ10"
          subheader="Bayesian スコア順"
        />
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>順位</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>名前</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>評価</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Bayesian スコア</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>レビュー数</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>3ヶ月内</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topRanking.length > 0 ? (
                  topRanking.map((item) => (
                    <TableRow key={item.target_id}>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {item.rank === 1 && '🥇'}
                        {item.rank === 2 && '🥈'}
                        {item.rank === 3 && '🥉'}
                        {item.rank > 3 && `#${item.rank}`}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.target_name}
                          </Typography>
                          <Chip
                            label={item.target_type === 'corporate' ? '企業' : '投稿ユーザー'}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20 }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Rating
                            value={item.avg_rating}
                            readOnly
                            size="small"
                            precision={0.1}
                          />
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {item.avg_rating.toFixed(1)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: item.bayesian_score >= 4 ? 'success.main' : 'text.primary'
                          }}
                        >
                          {item.bayesian_score.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {item.total_reviews}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.recent_reviews_3m}
                          size="small"
                          color={item.recent_reviews_3m > 0 ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        ランキングデータがありません
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* 統計情報 */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'primary.light' }}>
            <Typography variant="h6">
              {topRanking.length > 0 ? topRanking[0].total_reviews : 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              トップの総レビュー数
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'success.light' }}>
            <Typography variant="h6">
              {topRanking.length > 0 ? topRanking[0].avg_rating.toFixed(1) : 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              トップの平均評価
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'info.light' }}>
            <Typography variant="h6">
              {topRanking.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ランキング掲載数
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: 'warning.light' }}>
            <Typography variant="h6">
              {topRanking.length > 0 ? topRanking[0].recent_reviews_3m : 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              トップの3ヶ月内レビュー
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* 注記 */}
      <Alert severity="info" sx={{ mt: 3 }}>
        ℹ️ クチコミ分析機能は月1回（1日 0時00分 JST）に自動更新されます。月別トレンドデータは review_score_history コレクションに記録されます。
      </Alert>
    </Box>
  );
}
